/**
 * Role-Based Dashboard Component
 * Routes users to appropriate dashboard based on their assigned role.
 * Multi-role users (any combination of 2+ roles) see a hub so they can
 * choose which dashboard to enter.
 */

import { useNavigate, Navigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import PollIcon from '@mui/icons-material/Poll';
import SchoolIcon from '@mui/icons-material/School';
import BadgeIcon from '@mui/icons-material/Badge';
import { getCurrentUser } from '../auth/LoginButton';
import { ROLE_PRIORITY, ROLE_ROUTES } from '../rbac/roles';

const ROLE_LABELS: Record<string, string> = {
  'Survey.Admin':        'SIMS Admin',
  'Survey.Agent':        'SIMS Agent',
  'Survey.Creator':      'Survey Creator',
  'Campaign.Manager':    'Campaign Manager',
  'Graduate.Admin':      'Graduate Dashboard',
  'Graduate.Viewer':     'Graduate Dashboard',
  'Survey.Viewer':       'Survey Viewer',
  'Teachers':            'Teacher Surveys',
  'Faculty':             'Faculty Surveys',
  'Scheduler.Admin':     'Scheduler Admin',
  'Scheduler.Instructor':'Instructor Schedule',
  'Student':             'Student Portal',
};

function roleIcon(role: string) {
  if (role === 'Scheduler.Admin' || role === 'Scheduler.Instructor')
    return <CalendarMonthIcon sx={{ fontSize: 40, color: '#003366' }} />;
  if (role === 'Campaign.Manager')
    return <CampaignIcon sx={{ fontSize: 40, color: '#003366' }} />;
  if (role === 'Graduate.Admin' || role === 'Graduate.Viewer')
    return <SchoolIcon sx={{ fontSize: 40, color: '#003366' }} />;
  if (role === 'Survey.Admin' || role === 'Survey.Agent' || role === 'Survey.Creator' || role === 'Survey.Viewer')
    return <PollIcon sx={{ fontSize: 40, color: '#003366' }} />;
  if (role === 'Teachers' || role === 'Faculty')
    return <SchoolIcon sx={{ fontSize: 40, color: '#003366' }} />;
  if (role === 'Student')
    return <BadgeIcon sx={{ fontSize: 40, color: '#003366' }} />;
  return <DashboardIcon sx={{ fontSize: 40, color: '#003366' }} />;
}

// ── Hub shown when the user has 2+ known roles ───────────────────────────────
function MultiRoleHub({
  userRoles,
  primaryRole,
}: {
  userRoles: string[];
  primaryRole: string;
}) {
  const navigate = useNavigate();

  const cards = userRoles
    .filter(r => ROLE_ROUTES[r])
    .sort((a, b) => ROLE_PRIORITY.indexOf(a) - ROLE_PRIORITY.indexOf(b));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 3,
        p: 4,
      }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Choose your dashboard
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
        {cards.map(role => (
          <Card
            key={role}
            elevation={3}
            sx={{
              width: 220,
              cursor: 'pointer',
              border: role === primaryRole ? '2px solid #003366' : '2px solid transparent',
              '&:hover': { boxShadow: 8 },
            }}
            onClick={() => navigate(ROLE_ROUTES[role])}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                py: 3,
              }}
            >
              {roleIcon(role)}
              <Typography variant="subtitle1" fontWeight={600} textAlign="center">
                {ROLE_LABELS[role] ?? role}
              </Typography>
              {role === primaryRole && (
                <Typography variant="caption" color="text.secondary">
                  Primary
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1, backgroundColor: '#003366' }}
                onClick={e => { e.stopPropagation(); navigate(ROLE_ROUTES[role]); }}
              >
                Open
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────
export function RoleBasedDashboard() {
  const user = getCurrentUser();

  console.log('🔀 [RoleBasedDashboard] User:', user);
  console.log('🔀 [RoleBasedDashboard] Role:', user?.role);

  if (!user || !user.role) {
    console.log('🔀 [RoleBasedDashboard] No user or role, redirecting to login');
    return <Navigate to="/SIMS" replace />;
  }

  // Support both single role (legacy) and roles array (multi-role users)
  const userRoles: string[] = (user as any).roles?.length
    ? (user as any).roles
    : (user.role ? [user.role] : []);

  const primaryRole = ROLE_PRIORITY.find(r => userRoles.includes(r)) || user.role;
  const defaultRoute = ROLE_ROUTES[primaryRole] ?? '/Surveys/view';

  console.log('🔀 [RoleBasedDashboard] All roles:', userRoles);
  console.log('🔀 [RoleBasedDashboard] Primary role:', primaryRole);

  // Admins always go straight to their primary dashboard — no hub needed.
  const ADMIN_ROLES = ['Survey.Admin', 'Survey.Agent', 'Scheduler.Admin'];
  const isAdmin = userRoles.some(r => ADMIN_ROLES.includes(r));

  // Any non-admin user with 2+ known roles sees the hub so they can choose.
  const knownRoles = userRoles.filter(r => ROLE_ROUTES[r]);
  if (!isAdmin && knownRoles.length > 1) {
    return <MultiRoleHub userRoles={knownRoles} primaryRole={primaryRole} />;
  }

  console.log('🔀 [RoleBasedDashboard] Routing to:', defaultRoute);
  return <Navigate to={defaultRoute} replace />;
}
